import * as ToggleMenuFlyout from '@ori-events/toggle-menu-flyout';
import { useWindowSize } from '@ori/presentation-hooks';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';

import { useAppConfig } from '../../context';
import { useActiveMenu, useSearchInput } from '../../context/navigation';
import { useIsMobile } from '../../hooks';
import { useTranslations } from '../../hooks/useTranslations';
import { PopularProducts } from './PopularProducts';
import { PopularSearchQueries } from './PopularSearchQueries';
import {
  CustomBackdrop,
  SearchResultsContentWrapper,
  SearchResultsHeader,
  SearchResultsLeftSide,
  SearchResultsLoader,
  SearchResultsQuery,
  SearchResultsRoot,
} from './styles';

const LazyArticles = lazy(async () => ({ default: (await import('./Articles/Articles')).Articles }));

const SearchResultsContent: FC = () => {
  const isMobile = useIsMobile();
  const { formatMessage } = useTranslations();
  const { getTestId } = useTestId();
  const { onlyProductsInSearchResults } = useAppConfig();
  const { height } = useWindowSize();
  const { hideSearchResultsTab } = useActiveMenu();
  const { results, manualHide } = useSearchInput();
  const [innerHeight, setInnerHeight] = useState(height);
  const isArticles = Boolean(results?.Contents.length);

  const RenderSearchResult = () =>
    useMemo(
      () => (
        <>
          {results?.Query ? (
            <SearchResultsHeader data-testid={getTestId('search-results-header')}>
              {formatMessage('seeAllResultsFor')}
              <SearchResultsQuery
                href={`/search?query=${encodeURIComponent(results.Query)}`}
                data-testid={getTestId('search-results-query')}
              >
                {results.Query}
              </SearchResultsQuery>
            </SearchResultsHeader>
          ) : null}
          <PopularProducts />
          <SearchResultsLeftSide data-testid={getTestId('search-results-left-side')}>
            {!isMobile && <PopularSearchQueries />}
            <Suspense>{isArticles && !onlyProductsInSearchResults ? <LazyArticles /> : null}</Suspense>
          </SearchResultsLeftSide>
        </>
      ),
      [],
    );

  useEffect(() => {
    setInnerHeight(height);
  }, [height]);

  const onClickAway = useCallback(() => {
    manualHide();
    hideSearchResultsTab();
  }, [manualHide, hideSearchResultsTab]);

  return (
    <SearchResultsRoot data-testid={getTestId('search-results-root')}>
      <SearchResultsContentWrapper
        ownerState={{ innerHeight }}
        data-testid={getTestId('search-results-content-wrapper')}
      >
        {results ? (
          <RenderSearchResult />
        ) : (
          <SearchResultsLoader
            className="k-loading-image"
            data-testid={getTestId('search-results-loader')}
          />
        )}
      </SearchResultsContentWrapper>
      <CustomBackdrop
        open
        onClick={onClickAway}
      />
    </SearchResultsRoot>
  );
};

export const SearchResults: FC = () => {
  const { activeMenu } = useActiveMenu();
  const isMenuActive = activeMenu === ToggleMenuFlyout.MenuTypes.SearchResults;

  return isMenuActive ? <SearchResultsContent /> : null;
};
